/* @flow */
/* eslint-env es6,browser */

import { getRandom, apiVersion, generateUUID } from '../utils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import {
  isJSONSupported,
  getPageUrl,
  BrowserUtil,
  isDNT,
  isSecure,
  inIframe,
  getReferrer
} from '../utils/browserutil';
import { logError } from '../utils/logger';
import { extend, shuffle, unique } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';
import { getRTBUserEids } from '../idproviders/idputils';

const VERSION = '0.9'; // Sharethrough version
const SUPPLY_ID = 'WYu2BXv1';
const END_POINT = `https://btlr.sharethrough.com/universal/v1?supply_id=${SUPPLY_ID}`;

/**
 * @global
 * @typedef hb_data
 * @property {string} hb_pb Bid value
 * @property {string} hb_bidder Bidder name
 * @property {string} hb_adid Bid ID/creative
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * typedef definitions
 */
declare type slotData = {|
  imp: [
    {
      id: string,
      tagid: string,
      secure: number,
      ext: {
        tid: string
      },
      banner: {
        pos: number,
        topframe: number,
        format: Array<{
          w: number,
          h: number
        }>
      }
    }
  ]
|};

declare type siteInfo = {|
  site: {
    domain: string,
    page: string,
    ref: string,
    publisher: {
      domain: string
    }
  }
|};

declare type rtbUid = {|
  id: string,
  ext: {
    linkType: number
  },
  atype: number
|};

declare type rtbEid = {|
  source: string,
  uids: Array<rtbUid>
|};

declare type userInfo = {|
  user: {
    ext: {
      eids: Array<rtbEid>
    }
  }
|};

declare type regsInfo = {|
  regs: {
    coppa: number,
    ext: {
      us_privacy: string
    }
  }
|};

declare type sourceInfo = {|
  source: {
    tid: string,
    ext: {
      version: string,
      str: string
    }
  }
|};

declare type deviceInfo = {|
  device: {
    ua: string,
    language: string,
    js: number,
    dnt: number,
    h: number,
    w: number
  }
|};

declare type sharethroughReqBody = {|
  id: string,
  at: number,
  cur: Array<string>,
  tmax: number,
  ...siteInfo,
  ...deviceInfo,
  ...regsInfo,
  ...sourceInfo,
  bcat: Array<string>,
  badv: Array<string>,
  test: number,
  ...userInfo,
  imp: Array<slotData>
|};

declare type sharethroughBidRequestCache = {|
  impid: {
    [string]: string
  }
|};

class _sharethrough extends bidbase {
  _bidRequest: sharethroughBidRequestCache;

  _config: hbsharethroughConfig;

  _DefConfig: hbsharethroughConfig;

  // cookie sync URLs are part of the response, this is an array to accumulate the URLs for Sync
  _cookieSyncURLs: Array<string>;

  constructor(name: string = 'sharethrough') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      qpKey: 'sharethroughhb',
      bidUrl: END_POINT
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl'];
    this._cookieSyncURLs = [];
    this._bidRequest = {
      impid: {}
    };
  }

  _getImpressionForPos(pos: string): slotData {
    // get all the required params
    const sizes = [...this._config.posTokenMap[pos].sizes];
    const { tagid } = this._config.posTokenMap[pos];

    const format = sizes.map(([w, h]) => ({
      w,
      h
    }));

    const imp = {
      imp: [
        {
          id: getRandom(),
          tagid,
          secure: isSecure() ? 1 : 0,
          ext: {
            tid: generateUUID()
          },
          banner: {
            pos: 0,
            topframe: inIframe() ? 0 : 1,
            format
          }
        }
      ]
    };
    return imp;
  }

  _getSiteInfo(): siteInfo {
    return {
      site: {
        domain: window.location.host || '',
        page: getPageUrl(),
        ref: getReferrer(),
        publisher: {
          domain: window.location.host || ''
        }
      }
    };
  }

  _getRegs(): regsInfo {
    return {
      // similar to prebid (openRTB)
      regs: {
        coppa: 0,
        ext: {
          ...this._getPrivacyString()
        }
      }
    };
  }

  _getSource(): sourceInfo {
    return {
      source: {
        tid: generateUUID(),
        ext: {
          version: apiVersion(),
          str: VERSION
        }
      }
    };
  }

  _getDevice(): deviceInfo {
    return {
      device: {
        ua: BrowserUtil.getUserAgent(),
        // eslint-disable-next-line compat/compat
        language: navigator.language || '',
        js: 1, // (Hardcoded value from Prebid.js)
        dnt: isDNT() ? 1 : 0,
        h: window.screen.height,
        w: window.screen.width
      }
    };
  }

  _buildReqCommonBody(): sharethroughReqBody {
    const { user: { eids = [] } = {} } = getRTBUserEids();

    // build body common for all bid request
    const commonBody = extend(
      true,
      {},
      {
        at: 1, // (Hardcoded value from Prebid.js)
        cur: ['USD'],
        tmax: 5000
      },
      this._getSiteInfo(),
      this._getDevice(),
      this._getRegs(),
      this._getSource(),
      {
        bcat: [],
        badv: [],
        test: 0 // (Hardcoded value from Prebid.js)
      },
      {
        user: {
          ext: {
            eids
          }
        }
      }
    );
    return commonBody;
  }

  _getBid(pos: string, commonBody: sharethroughReqBody): Promise<boolean> {
    // get impression data for slot/position
    const impression = this._getImpressionForPos(pos);
    // generate unique ID and cache for response lookup later
    const id = getRandom();
    this._bidRequest.impid[id] = pos;
    const reqBody = extend(true, {}, commonBody, impression, {
      id
    });
    const req = {
      method: 'POST',
      body: JSON.stringify(reqBody),
      credentials: 'include'
    };

    const opt = {
      data: {}, // query params
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${pos}`
    };

    return fetchJSON(this._config.bidUrl, opt, req).then(
      (json: RTBBidResponse) => {
        this._bidResponses[pos] = json;
        return true;
      }
    );
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Array<Promise<boolean>> {
    // get common data for all requests
    const commonBody = this._buildReqCommonBody();
    // Clear the cookie sync url array before getting bids
    this._cookieSyncURLs = [];
    return posList.map(pos => this._getBid(pos, commonBody));
  }

  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const resp: RTBBidResponse = this._bidResponses[pos];

      const { cookieSyncUrls = [] } = resp;
      // append the cookie sync URLs from the response, to _cookieSyncURLs [ irrespective of whether we got bids ]
      this._cookieSyncURLs = [...this._cookieSyncURLs, ...cookieSyncUrls];

      if (!this._isValidRTBResponse(resp)) {
        return;
      }

      const {
        seatbid: [
          {
            bid: [res = {}]
          }
        ]
      } = resp;

      const { price: resPrice = 0, adm = '', impid = '', id = '' } = res;
      const price = String(resPrice);
      if (
        price.length > 0 &&
        adm.length > 0 &&
        (impid.length > 0 || id.length > 0)
      ) {
        this._handleBidPosData(ret, pos, price, impid.length ? impid : id, adm);
      }
      delete this._bidResponses[pos];
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - Header Bidding Positions list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    // get promise for all bid requests
    const pArray = this._getBids(posList);
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log errors for the failed promise
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }

  /**
   * This method will be called for bidders without overridden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const name = this.getName();
    const type = 'image';
    // syncUrls from the bid responses; exclude empty; get unique and shuffle [ User sync limit is currently 5 per bidder, this list has different sync URLs from response for each position ]
    const syncUrls = shuffle(
      unique(
        this._cookieSyncURLs
          .map(item => item.trim())
          .filter(item => item.length > 0)
      )
    );
    return syncUrls.map(url => ({
      name,
      type,
      url
    }));
  }
}

const sharethrough: _sharethrough = new _sharethrough();
export default sharethrough;
